/**
 * The six families of e-signature law.
 *
 * ⚠️ Every `url` below returned a success status when checked on 2026-08-10.
 * EUR-Lex answers automated requests with 202 rather than 200; both CELEX links
 * were confirmed live. Do not add a citation you have not opened.
 *
 * ⚠️ LAW ONLY. Nothing here says what SignForge produces under a family — that
 * is a delivery claim, it is counsel-gated, and it arrives in Phase 2.
 *
 * ── Family assignment rule (a country belongs to exactly one) ──
 * Pick the family that best explains the country's TIER STRUCTURE, because the
 * tiers are what determine whether we can reach the top one. Where two apply:
 *   1. `eidas` wins for EU/EEA states — the Regulation is directly applicable,
 *      so it governs regardless of what else the state has adopted.
 *   2. `licensed-provider` wins over `commonwealth-eta` / `uncitral-model`
 *      where the top tier is reserved to locally-licensed providers, since that
 *      reservation is the fact a reader needs. India sits here for that reason,
 *      not because its statute is unlike the Commonwealth shape.
 */
import type { LegalFamily } from "./types";

export const LEGAL_FAMILIES: LegalFamily[] = [
  // ── 1. eIDAS ─────────────────────────────────────────────────────────────
  {
    id: "eidas",
    slug: "eidas",
    name: "eIDAS",
    shortName: "eIDAS",
    tagline: "One directly-applicable Regulation across 30 countries.",
    summary:
      "Regulation (EU) No 910/2014 (eIDAS) establishes a single framework for electronic signatures across the European Union, and applies in the EEA states as well. It is a Regulation rather than a Directive, which means it applies directly in each member state without national transposition — so the same three-tier structure holds in every one of them. Article 25 provides that an electronic signature may not be denied legal effect or admissibility solely because it is electronic. Article 26 sets the four requirements an Advanced Electronic Signature must meet, and a Qualified Electronic Signature — the only tier the Regulation gives the equivalent legal effect of a handwritten signature — additionally requires a qualified certificate from a provider on an EU Trusted List and a qualified signature creation device.",
    instruments: [
      {
        name: "Regulation (EU) No 910/2014 (eIDAS)",
        url: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32014R0910",
      },
      {
        name: "Regulation (EU) 2024/1183 (European Digital Identity Framework)",
        url: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32024R1183",
        note: "Amends eIDAS. The three-tier signature structure is unchanged.",
      },
    ],
    tierModel: [
      {
        name: "Simple Electronic Signature",
        short: "SES",
        description:
          "Data in electronic form attached to or logically associated with other data and used by the signatory to sign. Cannot be denied legal effect solely for being electronic (Art. 25).",
      },
      {
        name: "Advanced Electronic Signature",
        short: "AdES",
        description:
          "Must be uniquely linked to the signatory, capable of identifying them, created using data the signatory can use under their sole control with a high level of confidence, and linked to the signed data so that any subsequent change is detectable (Art. 26).",
      },
      {
        name: "Qualified Electronic Signature",
        short: "QES",
        description:
          "An AdES created by a qualified signature creation device and based on a qualified certificate issued by a trust service provider on an EU Trusted List. Only this tier carries the equivalent legal effect of a handwritten signature across all member states.",
      },
    ],
  },

  // ── 2. United States ─────────────────────────────────────────────────────
  {
    id: "united-states",
    slug: "united-states",
    name: "United States",
    shortName: "United States",
    tagline: "Federal ESIGN over state UETA. No statutory tiers.",
    summary:
      "Two instruments operate together. The federal ESIGN Act (2000) provides that a signature, contract or record may not be denied legal effect solely because it is in electronic form, and applies to transactions in interstate or foreign commerce. UETA, a uniform act drafted by the Uniform Law Commission (1999), has been enacted in 49 states; New York instead has its own Electronic Signatures and Records Act. ESIGN preempts inconsistent state law but yields to a state's enactment of UETA. Neither instrument grades signatures into tiers — the question is whether the signature was executed with intent to sign and is attributable to the signer, which is proved by the surrounding record rather than by the signature's technical form. Both carve out categories including wills, testamentary trusts, and certain family-law and Uniform Commercial Code matters.",
    instruments: [
      {
        name: "ESIGN Act, Pub. L. 106-229 (2000)",
        url: "https://www.govinfo.gov/content/pkg/PLAW-106publ229/html/PLAW-106publ229.htm",
      },
      {
        name: "ESIGN Act as codified — 15 U.S.C. ch. 96",
        url: "https://www.law.cornell.edu/uscode/text/15/chapter-96",
      },
      {
        name: "Uniform Electronic Transactions Act (1999)",
        url: "https://www.uniformlaws.org/committees/community-home?CommunityKey=2c04b76c-2b7d-4399-977e-d5876ba7e034",
        note: "Enacted in 49 states; New York has ESRA instead.",
      },
    ],
    tierModel: [],
    tierNote:
      "Neither ESIGN nor UETA grades electronic signatures into tiers. Enforceability turns on intent and attribution, evidenced by the record surrounding the signature.",
  },

  // ── 3. UNCITRAL Model Law adopters ───────────────────────────────────────
  {
    id: "uncitral-model",
    slug: "uncitral-model-law",
    name: "UNCITRAL Model Law adopters",
    shortName: "UNCITRAL",
    tagline: "88 States on a shared template, with a published adoption table.",
    summary:
      "UNCITRAL's Model Law on Electronic Commerce (1996) is not itself law anywhere. It is a template national legislatures adopt, and UNCITRAL maintains a status table recording every State that has enacted legislation based on or influenced by it — 88 States and 171 jurisdictions, the difference being that jurisdictions counts sub-national units such as US states and Canadian provinces. The companion Model Law on Electronic Signatures (2001), adopted in fewer States, adds criteria for when a signature method is considered reliable. Because adoption is recorded in a published table, membership of this family is citable without a separate reading of each national statute — which is why countries can be listed here honestly before their individual statute has been sourced.",
    instruments: [
      {
        name: "UNCITRAL Model Law on Electronic Commerce (1996) — status table",
        url: "https://uncitral.un.org/en/texts/ecommerce/modellaw/electronic_commerce/status",
        note: "88 States, 171 jurisdictions.",
      },
      {
        name: "UNCITRAL Model Law on Electronic Signatures (2001) — status table",
        url: "https://uncitral.un.org/en/texts/ecommerce/modellaw/electronic_signatures/status",
        note: "40 States, 42 jurisdictions.",
      },
    ],
    tierModel: [],
    tierNote:
      "The Model Law itself does not tier signatures. Most adopting States distinguish an ordinary electronic signature from a stronger form — variously 'advanced', 'qualified' or 'certified' — but the naming and the legal consequence differ by State, so the tier structure is recorded per country rather than for the family.",
  },

  // ── 4. Commonwealth Electronic Transactions Acts ─────────────────────────
  {
    id: "commonwealth-eta",
    slug: "commonwealth-eta",
    name: "Commonwealth Electronic Transactions Acts",
    shortName: "Commonwealth ETA",
    tagline: "Shared statutory DNA across common-law jurisdictions.",
    summary:
      "A group of common-law jurisdictions enacted Electronic Transactions Acts with a closely related structure, most drawing on the UNCITRAL Model Law and on each other. The shared shape is functional equivalence: a legal requirement for a signature is satisfied by an electronic method that identifies the person and indicates their intention, provided the method was as reliable as appropriate in the circumstances, and — in several of these Acts — that the recipient consented to it. The Acts are generally technology-neutral and do not privilege one signing method, so evidential weight is established by the surrounding record. Excluded categories differ by jurisdiction and commonly include wills, powers of attorney and certain property instruments.",
    instruments: [
      {
        name: "United Kingdom — Electronic Communications Act 2000",
        url: "https://www.legislation.gov.uk/ukpga/2000/7/contents",
      },
      {
        name: "Australia — Electronic Transactions Act 1999 (Cth)",
        url: "https://www.legislation.gov.au/C2004A00553/latest/text",
        note: "States and territories have corresponding Acts.",
      },
    ],
    tierModel: [],
    tierNote:
      "These Acts are generally technology-neutral and do not define signature tiers. Where a jurisdiction in this family also operates a licensed certification authority scheme, it is placed in the licensed-provider family instead, because that reservation is the more consequential fact.",
  },

  // ── 5. Civil-law tiered regimes outside eIDAS ────────────────────────────
  {
    id: "civil-law-tiered",
    slug: "civil-law-tiered",
    name: "Civil-law tiered regimes",
    shortName: "Civil-law tiered",
    tagline: "Tiered like eIDAS, but under national law.",
    summary:
      "Several civil-law jurisdictions outside the EU operate their own tiered frameworks, structurally similar to eIDAS but established nationally and not mutually recognised with it. Brazil is the clearest example: Provisional Measure 2.200-2/2001 created the ICP-Brasil public key infrastructure, and Law 14.063/2020 subsequently defined three classes of electronic signature — simple, advanced and qualified — and set which class is required for which category of interaction, particularly with public bodies. The practical consequence in this family is that the top tier is tied to a national infrastructure, so a signature produced outside that infrastructure sits in a lower class regardless of its cryptographic strength.",
    instruments: [
      {
        name: "Brazil — Medida Provisória 2.200-2/2001 (ICP-Brasil)",
        url: "https://www.planalto.gov.br/ccivil_03/mpv/antigas_2001/2200-2.htm",
      },
      {
        name: "Brazil — Lei 14.063/2020",
        url: "https://www.planalto.gov.br/ccivil_03/_ato2019-2022/2020/lei/l14063.htm",
        note: "Defines simple, advanced and qualified classes.",
      },
    ],
    tierModel: [],
    tierNote:
      "Tiers exist but are defined nationally and are not mutually recognised across this family, so they are recorded per country. ⚠️ Japan and South Korea belong here; their instruments are added in T2 once an accessible primary source is confirmed — the standard Japanese translation host refused automated retrieval on 2026-08-10 and no unverified URL is recorded.",
  },

  // ── 6. Licensed-provider regimes ─────────────────────────────────────────
  {
    id: "licensed-provider",
    slug: "licensed-provider-regimes",
    name: "Licensed-provider regimes",
    shortName: "Licensed-provider",
    tagline: "The top tier is reserved to locally-licensed providers.",
    summary:
      "In these jurisdictions ordinary electronic signatures are recognised, but a higher evidentiary status — often a presumption of authenticity or non-repudiation in court — is reserved to signatures issued through providers licensed, accredited or registered locally. India is representative: the Information Technology Act 2000, as amended in 2008, recognises electronic signatures generally, while digital signatures issued under certifying authorities licensed by the Controller of Certifying Authorities carry the stronger statutory position. The distinction matters commercially rather than legally: a signature from an unlicensed provider remains valid and admissible, but the party relying on it carries the evidential burden that the reserved tier would otherwise have discharged.",
    instruments: [
      {
        name: "India — Information Technology Act, 2000",
        url: "https://www.indiacode.nic.in/handle/123456789/1999",
        note: "Amended 2008 to broaden 'digital signature' to 'electronic signature'.",
      },
    ],
    tierModel: [],
    tierNote:
      "Two effective tiers: an ordinary electronic signature, and a stronger form reserved to locally-licensed providers. ⚠️ Further instruments — China, Russia, Vietnam, Guatemala and others — are added in T2 as each primary source is verified. The Guatemalan RPSC copy of Decreto 47-2008 returned 404 on 2026-08-10 and is deliberately not cited.",
  },
];
